import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ReceiptType } from "@KassaroAB/shared/types/enums/receiptType";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import {
  useGetStatsDayQuery,
  useGetStatsWeekQuery,
  useGetStatsMonthQuery,
  useGetStatsProductsQuery,
  useGetStatsGroupsQuery,
  useGetChainStatsStoresQuery,
} from "../../query/statsQuery";
import type { StatsScope } from "../../hooks/useStatsScope";
import { statsPath } from "../../hooks/useStatsScope";
import { formatDateTime } from "../../utils/dateUtils";
import type { ChainStatsDB, ChainStatsStoreDB } from "@KassaroAB/shared/types/storeStats";
import { useGetActiveFeaturesQuery } from "../../query/billingItemsQuery";
import { useGetStoreQuery } from "../../query/storesQuery";
import { useGetPaymentMethodsQuery } from "../../query/paymentMethodsQuery";
import { PaymentReportGrouping } from "@KassaroAB/shared/types/enums/PaymentReportGrouping";
import {
  groupPaymentMethodTotals,
  paymentReportGroupKey,
  resolvePaymentReportGrouping,
} from "@KassaroAB/shared/utils/receiptCalculations";
import { formatMoney } from "../../utils/formatMoney";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import { SectionTitle } from "../../components/SectionTitle";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/Table";
import { Button } from "../../components/Button";
import type { StatsPeriod } from "../../api/statsApi";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const formatPeriodLabel = (
  selectedId: string,
  period: StatsPeriod,
  weekLabel: (n: number) => string
): string => {
  if (period === "days") {
    return DateTime.fromISO(selectedId).toFormat("d MMM yyyy");
  }
  if (period === "weeks") {
    const weekNumber = parseInt(selectedId.match(/W(\d+)/)?.[1] ?? "0", 10);
    const year = selectedId.match(/^(\d{4})/)?.[1] ?? "";
    return year ? `${weekLabel(weekNumber)} ${year}` : weekLabel(weekNumber);
  }
  return DateTime.fromFormat(`${selectedId}-01`, "yyyy-MM-dd").toFormat(
    "MMM yyyy"
  );
};

const receiptTypeLabels: Record<ReceiptType, string> = {
  [ReceiptType.RECEIPT]: "statistics.receiptType.RECEIPT",
  [ReceiptType.REFUND]: "statistics.receiptType.REFUND",
  [ReceiptType.PAYOUT]: "statistics.receiptType.PAYOUT",
  [ReceiptType.INVOICE]: "statistics.receiptType.INVOICE",
  [ReceiptType.ORDER]: "statistics.receiptType.ORDER",
};

const DateHeader = styled.h3`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  text-align: center;
  margin: 0 0 ${spacing.x1}px 0;
  color: ${colors.text};
`;

const UpdatedAt = styled.p`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  text-align: center;
  margin: 0 0 ${spacing.x2}px 0;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
  margin-bottom: ${spacing.x2}px;
`;

const OverviewBox = styled(Card)`
  flex: 1;
  min-width: 140px;
  padding: ${spacing.x2}px;
`;

const OverviewLabel = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin-bottom: ${spacing.x05}px;
`;

const OverviewValue = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const TopSoldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
  margin-bottom: ${spacing.x2}px;
`;

const TopSoldLink = styled(Link)`
  flex: 1;
  min-width: 140px;
  text-decoration: none;
  color: inherit;
`;

const TopSoldBox = styled(Card)`
  padding: ${spacing.x2}px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const TopSoldTitle = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin-bottom: ${spacing.x05}px;
`;

const TopSoldName = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  margin-bottom: ${spacing.x05}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TopSoldValue = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
`;

const BoxSection = styled(Card)`
  background-color: ${colors.background};
  margin-bottom: ${spacing.x2}px;
`;

const ErrorBox = styled.div`
  padding: ${spacing.x3}px;
  text-align: center;
  color: ${colors.danger};
`;

const StoresHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x1}px;
`;

const ClearStoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${colors.primary};
  font-size: ${fontSizes.small}px;
  cursor: pointer;
  text-decoration: underline;
`;

interface StatsDetailContentProps {
  scope: StatsScope;
  period: StatsPeriod;
  selectedId: string;
  from: string;
  to: string;
}

export const StatsDetailContent = ({
  scope,
  period,
  selectedId,
  from,
  to,
}: StatsDetailContentProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedStoreId = searchParams.get("store");
  const { data: activeFeatures } = useGetActiveFeaturesQuery(
    scope.type === "store" ? scope.id : null
  );
  const groupingStoreId =
    scope.type === "store" ? scope.id : (focusedStoreId ?? undefined);
  const { data: groupingStore } = useGetStoreQuery(groupingStoreId);
  const { data: paymentMethods } = useGetPaymentMethodsQuery(groupingStoreId);

  const { data: mostSoldProducts } = useGetStatsProductsQuery(
    scope,
    period,
    selectedId,
    { limit: 25, storeId: focusedStoreId ?? undefined }
  );

  const { data: mostSoldGroups } = useGetStatsGroupsQuery(
    scope,
    period,
    selectedId,
    { limit: 25, storeId: focusedStoreId ?? undefined }
  );

  const { data: storeSnapshots } = useGetChainStatsStoresQuery(
    scope.type === "chain" ? scope.id : undefined,
    period,
    selectedId
  );

  const dayQuery = useGetStatsDayQuery(
    scope,
    period === "days" ? selectedId : undefined
  );
  const weekQuery = useGetStatsWeekQuery(
    scope,
    period === "weeks" ? selectedId : undefined
  );
  const monthQuery = useGetStatsMonthQuery(
    scope,
    period === "months" ? selectedId : undefined
  );

  const queries = { days: dayQuery, weeks: weekQuery, months: monthQuery };
  const query = queries[period];
  const { data: stats, isLoading, isError, refetch } = query;

  if (isLoading) {
    return <LoadingContainer message={t("statistics.loading")} />;
  }

  if (isError) {
    return (
      <ErrorBox>
        <p>{t("statistics.couldNotLoadReport")}</p>
        <Button onClick={() => refetch()}>{t("statistics.retry")}</Button>
      </ErrorBox>
    );
  }

  if (!stats) {
    return null;
  }

  const focusedStore = (storeSnapshots ?? []).find(
    (store) => store.storeId === focusedStoreId
  );
  const displayStats = focusedStore ?? stats;
  const currency = displayStats.receipts.grossAmount.currency;
  const totalRefunds = displayStats.receiptTypes[ReceiptType.REFUND]
    ?.grossAmount ?? {
    amount: 0,
    currency,
  };

  const paymentRows = groupPaymentMethodTotals(
    displayStats.paymentMethods,
    groupingStoreId
      ? resolvePaymentReportGrouping(groupingStore ?? {})
      : PaymentReportGrouping.PROVIDER,
    (id) => paymentMethods?.find((pm) => pm.id === id)?.type,
    (group) => t(paymentReportGroupKey(group))
  );
  const mostSoldProduct = mostSoldProducts?.[0];
  const mostSoldGroup = mostSoldGroups?.[0];
  const chainStats = stats as ChainStatsDB;
  const statsBasePath = `/${statsPath(scope)}/statistics`;
  const storeQuery = focusedStoreId ? `&store=${focusedStoreId}` : "";
  const sortedStores = [...(storeSnapshots ?? [])].sort(
    (a, b) => b.receipts.grossAmount.amount - a.receipts.grossAmount.amount
  );
  const chainGross = stats.receipts.grossAmount.amount;

  const setFocusedStoreId = (storeId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (storeId) {
      next.set("store", storeId);
    } else {
      next.delete("store");
    }
    setSearchParams(next, { replace: true });
  };

  const formatShare = (amount: number) => {
    if (chainGross <= 0) {
      return "0%";
    }
    return `${Math.round((amount / chainGross) * 1000) / 10}%`;
  };

  const storeRefunds = (store: ChainStatsStoreDB) =>
    store.receiptTypes[ReceiptType.REFUND]?.grossAmount ?? {
      amount: 0,
      currency: store.receipts.grossAmount.currency,
    };

  const visibleReceiptTypes = Object.entries(displayStats.receiptTypes).filter(
    ([type, total]) => {
      if (
        type === ReceiptType.INVOICE &&
        !activeFeatures?.includes(Features.INVOICE) &&
        total.count === 0
      ) {
        return false;
      }
      if (
        type === ReceiptType.ORDER &&
        !activeFeatures?.includes(Features.ORDER) &&
        total.count === 0
      ) {
        return false;
      }
      return true;
    }
  );

  return (
    <>
      <DateHeader>
        {formatPeriodLabel(selectedId, period, (n) =>
          t("statistics.weekLabel", { number: n })
        )}
      </DateHeader>
      {displayStats.computedAt && (
        <UpdatedAt>
          {t("statistics.lastUpdated", {
            time: formatDateTime(displayStats.computedAt),
          })}
          {scope.type === "chain" &&
          !focusedStore &&
          chainStats.storeCount !== undefined &&
          chainStats.storesWithStats < chainStats.storeCount
            ? ` · ${t("statistics.storesIncluded", {
                included: chainStats.storesWithStats,
                total: chainStats.storeCount,
              })}`
            : null}
          {focusedStore
            ? ` · ${t("statistics.showingStore", { name: focusedStore.storeName })}`
            : null}
        </UpdatedAt>
      )}

      <OverviewRow>
        <OverviewBox>
          <OverviewLabel>{t("statistics.overviewTotalSales")}</OverviewLabel>
          <OverviewValue>
            {formatMoney(displayStats.receipts.grossAmount)}
          </OverviewValue>
        </OverviewBox>
        <OverviewBox>
          <OverviewLabel>{t("statistics.overviewTotalRefunds")}</OverviewLabel>
          <OverviewValue>{formatMoney(totalRefunds)}</OverviewValue>
        </OverviewBox>
        <OverviewBox>
          <OverviewLabel>{t("statistics.overviewTotalDiscounts")}</OverviewLabel>
          <OverviewValue>
            {formatMoney(displayStats.discounts.amount)}
          </OverviewValue>
        </OverviewBox>
        <OverviewBox>
          <OverviewLabel>{t("statistics.overviewReceiptCount")}</OverviewLabel>
          <OverviewValue>{displayStats.receipts.count}</OverviewValue>
        </OverviewBox>
      </OverviewRow>

      {scope.type === "chain" && sortedStores.length > 0 && (
        <BoxSection>
          <StoresHeader>
            <SectionTitle as="h4">{t("statistics.stores")}</SectionTitle>
            {focusedStoreId && (
              <ClearStoreButton
                type="button"
                onClick={() => setFocusedStoreId(null)}
              >
                {t("statistics.clearStoreFilter")}
              </ClearStoreButton>
            )}
          </StoresHeader>
          <Table minWidth="640px">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  {t("statistics.storeColumnName")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.overviewTotalSales")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.overviewTotalRefunds")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.overviewTotalDiscounts")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.overviewReceiptCount")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.storeShare")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedStores.map((store) => (
                <TableRow
                  key={store.storeId}
                  clickable
                  active={store.storeId === focusedStoreId}
                  onClick={() =>
                    setFocusedStoreId(
                      store.storeId === focusedStoreId ? null : store.storeId
                    )
                  }
                >
                  <TableCell>{store.storeName}</TableCell>
                  <TableCell align="right">
                    {formatMoney(store.receipts.grossAmount)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(storeRefunds(store))}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(store.discounts.amount)}
                  </TableCell>
                  <TableCell align="right">{store.receipts.count}</TableCell>
                  <TableCell align="right">
                    {formatShare(store.receipts.grossAmount.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </BoxSection>
      )}

      {(mostSoldProduct || mostSoldGroup) && (
        <TopSoldRow>
          {mostSoldProduct && (
            <TopSoldLink
              to={`${statsBasePath}/products/${period}/${selectedId}?from=${from}&to=${to}&period=${period}&selected=${selectedId}${storeQuery}`}
            >
              <TopSoldBox>
                <TopSoldTitle>{t("statistics.mostSoldProducts")}</TopSoldTitle>
                <TopSoldName>
                  {mostSoldProduct.name ?? mostSoldProduct.productId}
                </TopSoldName>
                <TopSoldValue>
                  {mostSoldProduct.quantity} {t("statistics.quantityUnit")},{" "}
                  {formatMoney(mostSoldProduct.grossAmount)}
                </TopSoldValue>
              </TopSoldBox>
            </TopSoldLink>
          )}
          {mostSoldGroup && (
            <TopSoldLink
              to={`${statsBasePath}/groups/${period}/${selectedId}?from=${from}&to=${to}&period=${period}&selected=${selectedId}${storeQuery}`}
            >
              <TopSoldBox>
                <TopSoldTitle>{t("statistics.mostSoldGroups")}</TopSoldTitle>
                <TopSoldName>
                  {mostSoldGroup.name ?? mostSoldGroup.groupId}
                </TopSoldName>
                <TopSoldValue>
                  {mostSoldGroup.quantity} {t("statistics.quantityUnit")},{" "}
                  {formatMoney(mostSoldGroup.grossAmount)}
                </TopSoldValue>
              </TopSoldBox>
            </TopSoldLink>
          )}
        </TopSoldRow>
      )}

      {visibleReceiptTypes.length > 0 && (
        <BoxSection>
          <SectionTitle as="h4">{t("statistics.receiptTypes")}</SectionTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  {t("statistics.receiptTypesColumnType")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.receiptTypesColumnCount")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.receiptTypesColumnAmount")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleReceiptTypes.map(([type, total]) => (
                <TableRow key={type}>
                  <TableCell>
                    {t(receiptTypeLabels[type as ReceiptType] ?? type)}
                  </TableCell>
                  <TableCell align="right">{total.count}</TableCell>
                  <TableCell align="right">
                    {formatMoney(total.grossAmount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </BoxSection>
      )}

      {paymentRows.length > 0 && (
        <BoxSection>
          <SectionTitle as="h4">
            {t("statistics.salesByPaymentMethod")}
          </SectionTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  {t("statistics.receiptTypesColumnType")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.receiptTypesColumnCount")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.receiptTypesColumnAmount")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paymentRows.map((total) => (
                <TableRow key={total.key}>
                  <TableCell>{total.name ?? total.key}</TableCell>
                  <TableCell align="right">{total.count}</TableCell>
                  <TableCell align="right">
                    {formatMoney(total.grossAmount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </BoxSection>
      )}

      {displayStats.hourOfDay.receiptsCount.some((c) => c > 0) && (
        <BoxSection>
          <SectionTitle as="h4">{t("statistics.hourOfDay")}</SectionTitle>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  {t("statistics.receiptTypesColumnType")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.receiptTypesColumnCount")}
                </TableHeaderCell>
                <TableHeaderCell align="right">
                  {t("statistics.receiptTypesColumnAmount")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayStats.hourOfDay.receiptsCount.map((count, hour) => {
                if (count === 0) return null;
                const amount = displayStats.hourOfDay.grossAmount[hour];
                return (
                  <TableRow key={hour}>
                    <TableCell>
                      {hour}:00 - {hour + 1}:00
                    </TableCell>
                    <TableCell align="right">{count}</TableCell>
                    <TableCell align="right">
                      {formatMoney(amount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </BoxSection>
      )}
    </>
  );
};
